import React from 'react';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import FormGroup from 'shared/ui/FormGroup/FormGroup';
import Select from 'shared/ui/Select/Select';
import InputText from 'shared/ui/Input/InputText';
import InputNumber from 'shared/ui/Input/InputNumber/InputNumber';
import Checkbox from 'shared/ui/Checkbox/Checkbox';
import {CollapsiblePanel} from 'entities/tariff';
import HintQuestion from 'shared/ui/Hint/HintQuestion';
import IServerAlertRule from 'features/server/model/ServerAlertRule/IServerAlertRule';
import useServerAlertRuleForm from 'features/server/model/useServerAlertRuleForm';

import styles from './ServerAlertRuleForm.module.scss';

interface IServerAlertRuleFormProps {
    item?: IServerAlertRule,
    presetServerUuid?: string,
    onSuccess: () => void,
    onClose?: () => void,
}

const ServerAlertRuleForm: React.FC<IServerAlertRuleFormProps> = ({item, presetServerUuid, onSuccess, onClose}) => {
    const {
        isNew, control, register, setValue, errors, isValid, isSaving, submit,
        serverOptions, metricOptions, operatorOptions, severityOptions, requiresScope, metricRecommendation, defaults,
    } = useServerAlertRuleForm({item, presetServerUuid, onSuccess});

    return (
        <form onSubmit={submit} className={styles.form}>
            <ModalBodyWrapper className={styles.wrapper}>
                <FormGroupWithError error={errors?.server?.message}>
                    <label className={styles.label}>{t`Область действия`}</label>
                    <Select
                        control={control}
                        name="server"
                        data={serverOptions}
                        rules={{required: true}}
                    />
                </FormGroupWithError>

                <FormGroupWithError error={errors?.metric?.message}>
                    <label className={styles.label}>{t`Метрика`}</label>
                    <Select
                        control={control}
                        name="metric"
                        data={metricOptions}
                        rules={{required: t`Выберите метрику`}}
                    />
                </FormGroupWithError>

                {
                    requiresScope && (
                        <FormGroupWithError error={errors?.scope?.message}>
                            <label className={styles.label}>{t`Точка монтирования`}</label>
                            <InputText
                                control={control}
                                register={register}
                                name="scope"
                                placeholder="/var"
                            />
                        </FormGroupWithError>
                    )
                }

                <div className={styles.row}>
                    <FormGroupWithError error={errors?.operator?.message} className={styles.operatorGroup}>
                        <label className={styles.label}>{t`Условие`}</label>
                        <Select
                            control={control}
                            name="operator"
                            data={operatorOptions}
                            rules={{required: true}}
                            searchable={false}
                        />
                    </FormGroupWithError>

                    <FormGroupWithError error={errors?.threshold?.message} className={styles.thresholdGroup}>
                        <label className={styles.label}>{t`Значение`}</label>
                        <InputNumber
                            control={control}
                            name="threshold"
                            setValue={setValue}
                            defaultValue={item?.threshold ?? metricRecommendation?.threshold ?? 0}
                        />
                    </FormGroupWithError>
                </div>

                <FormGroupWithError error={errors?.severity?.message}>
                    <label className={styles.label}>{t`Важность`}</label>
                    <Select
                        control={control}
                        name="severity"
                        data={severityOptions}
                        rules={{required: true}}
                        searchable={false}
                    />
                </FormGroupWithError>

                <FormGroup>
                    <Checkbox {...register('enabled')}>
                        {t`Правило включено`}
                    </Checkbox>
                </FormGroup>
            </ModalBodyWrapper>
            <ModalBodyWrapper className={styles.wrapper}>
                <CollapsiblePanel title={t`Продвинутые настройки`} textStyle={styles.advancedTitle}>
                    <div className={styles.row}>
                        <FormGroupWithError error={errors?.for_minutes?.message}>
                            <label className={cn(styles.label, styles.labelWithHint)}>
                                {t`До открытия инцидента, мин.`}
                                <HintQuestion placement="top">
                                    {/* eslint-disable-next-line max-len */}
                                    {t`Сколько минут подряд метрика должна нарушать условие, прежде чем будет открыт инцидент. Помогает не реагировать на короткие всплески нагрузки.`}
                                </HintQuestion>
                            </label>
                            <InputNumber
                                control={control}
                                name="for_minutes"
                                setValue={setValue}
                                min={5}
                                step={1}
                                defaultValue={defaults.for_minutes}
                            />
                        </FormGroupWithError>

                        <FormGroupWithError error={errors?.recover_minutes?.message}>
                            <label className={cn(styles.label, styles.labelWithHint)}>
                                {t`До закрытия инцидента, мин.`}
                                <HintQuestion placement="top">
                                    {/* eslint-disable-next-line max-len */}
                                    {t`Сколько минут подряд метрика должна держаться в норме, прежде чем инцидент закроется сам. Защищает от «мигания» инцидентов на границе значения.`}
                                </HintQuestion>
                            </label>
                            <InputNumber
                                control={control}
                                name="recover_minutes"
                                setValue={setValue}
                                min={1}
                                step={1}
                                defaultValue={defaults.recover_minutes}
                            />
                        </FormGroupWithError>
                    </div>
                </CollapsiblePanel>
            </ModalBodyWrapper>
            <ModalBlockButton
                primaryButtonText={isNew ? t`Добавить` : t`Сохранить`}
                primaryButtonType="submit"
                primaryButtonLoading={isSaving}
                primaryButtonDisabled={!isValid}
                secondaryButtonOnClick={onClose}
            />
        </form>
    );
};

export default ServerAlertRuleForm;
